import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getStatsService } from '@src/container';
import type { StatsDTO } from '@src/application/stats/dto';

/**
 * Hook: useStats
 * Fetches current application statistics
 * 
 * @param options - React Query options
 * @returns Query result containing stats data
 * 
 * @example
 * ```typescript
 * const { data: stats, isLoading, error, refetch } = useStats({
 *     refetchInterval: 30000, // Refetch every 30 seconds
 *     staleTime: 10000, // Consider data stale after 10 seconds
 * });
 * 
 * if (isLoading) return <div>Loading stats...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * 
 * return (
 *     <div>
 *         <p>Total PASCOs: {stats?.pasco.total_pascos}</p>
 *         <p>Total Questions: {stats?.pasco.total_question_chunks}</p>
 *         <p>Completed: {stats?.pasco.status_breakdown?.completed}</p>
 *     </div>
 * );
 * ```
 */
export function useStats(
    options?: Omit<
        UseQueryOptions<StatsDTO, Error>,
        'queryKey' | 'queryFn'
    >
) {
    const statsService = getStatsService();

    return useQuery<StatsDTO, Error>({
        queryKey: ['stats'],
        queryFn: async () => {
            return await statsService.getStats();
        },
        // Default options - can be overridden by passing options parameter
        staleTime: 5 * 60 * 1000, // 5 minutes - stats don't change frequently
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        ...options,
    });
}
